import { DatePicker, TimePicker } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'
import React, { useState } from 'react'

interface Props {
  start: Date
  end: Date
  /** Emit the new [start, end] pair. In single-day mode both are equal. */
  onChange: (start: Date, end: Date) => void
}

/** Start and end within a minute of each other read as a single day. */
const SINGLE_THRESHOLD_MS = 60_000
export const isSingleDay = (start: Date, end: Date) =>
  Math.abs(start.getTime() - end.getTime()) < SINGLE_THRESHOLD_MS

/** Replace the calendar day of `base` with `picked`, keeping `base`'s time. */
const withDay = (picked: Date, base: Date) =>
  set(picked, {
    year: picked.getFullYear(),
    month: picked.getMonth(),
    date: picked.getDate(),
    hours: base.getHours(),
    minutes: base.getMinutes(),
    seconds: base.getSeconds(),
  })

const pickers = 'flex flex-wrap items-center gap-2 [&>*+*]:ml-0'
const subLabel = 'mb-1 text-xs font-semibold uppercase text-stone-500'
const toggleBtn =
  'mt-1 self-start text-sm font-medium text-[#003057] hover:underline'

/**
 * Date field with two modes:
 *   - Single day (default): one time + date input; start === end.
 *   - Date range: separate from/to time + date inputs.
 * An existing issue whose start and end differ opens directly in range mode.
 */
export const IssueDateRangeField: React.FC<Props> = ({
  start,
  end,
  onChange,
}) => {
  const translations = useTranslate({
    dataFrom: 'common.data-from',
    dataTo: 'common.data-to',
    useRange: 'issues.use-date-range',
    useSingle: 'issues.use-single-day',
  })

  // null → follow the data (range only when start/end differ, e.g. an existing
  // multi-day issue); true/false → the user's explicit choice.
  const [userMode, setUserMode] = useState<boolean | null>(null)
  const rangeMode = userMode ?? !isSingleDay(start, end)

  if (!rangeMode) {
    const setSingle = (d: Date) => onChange(d, d)
    return (
      <div className="flex flex-col">
        <div className={pickers}>
          <TimePicker value={start} onChange={setSingle} />
          <DatePicker
            mode="single"
            value={start}
            onChange={d => (d ? setSingle(withDay(d, start)) : undefined)}
          />
        </div>
        <button
          type="button"
          className={toggleBtn}
          onClick={() => setUserMode(true)}
        >
          {translations.useRange}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className={subLabel}>{translations.dataFrom}</div>
        <div className={pickers}>
          <TimePicker
            value={start}
            onChange={v => onChange(v, v > end ? v : end)}
          />
          <DatePicker
            mode="single"
            value={start}
            onChange={d => {
              if (!d) return
              const s = withDay(d, start)
              onChange(s, s > end ? s : end)
            }}
          />
        </div>
      </div>
      <div>
        <div className={subLabel}>{translations.dataTo}</div>
        <div className={pickers}>
          <TimePicker value={end} onChange={v => onChange(start, v)} />
          <DatePicker
            mode="single"
            value={end}
            onChange={d => (d ? onChange(start, withDay(d, end)) : undefined)}
          />
        </div>
      </div>
      <button
        type="button"
        className={toggleBtn}
        onClick={() => {
          setUserMode(false)
          onChange(start, start)
        }}
      >
        {translations.useSingle}
      </button>
    </div>
  )
}
